// Pure movement math (no Phaser): the per-mech MovementProfile scaled by the
// current stats, plus the small helpers the controller and the view share.
// Everything here is deterministic and unit-tested (tests/player-movement.test.ts).

import type { MovementProfile, StatBlock } from '../../core/types';

/**
 * Feel constants owned by the player module (they are not per-mech, so they do
 * not live in data/mechs.ts). Times in seconds, speeds in px/s.
 */
export const FEEL = {
  /** A dash press this long before the dash is ready still triggers. */
  dashBuffer: 0.1,
  /** Horizontal speed carried out of a dash, × runSpeed (a touch of momentum, then normal control). */
  dashExitSpeed: 1.1,
  /** Horizontal control multiplier during dash recovery. */
  recoveryControl: 0.35,
  /** Downward velocity during a grounded dash so floor contact (and ledge detection) stays exact. */
  dashGroundStick: 40,
  /** A dash that has hit a wall for this long ends early. */
  dashWallStop: 0.02,
  /** Extra dash i-frames with the PHASE_CAPACITOR mutation. */
  phaseCapacitorIFrames: 0.12,
  /** Reduced horizontal control after a knockback so the impulse actually displaces the mech. */
  knockbackTime: 0.16,
  knockbackControl: 0.25,
  /** Deceleration (px/s²) from a fast fall down to the glide speed. */
  glideBrake: 4200,
  /** Down + jump on a one-way platform: pass-through window and initial push. */
  dropTime: 0.25,
  dropKick: 160,
  /** Time the jump sheet spends on its takeoff frames. */
  takeoffTime: 0.07,
  /** Landing squash / jump stretch limits (fraction of height). */
  maxSquash: 0.08,
  jumpStretch: 0.06,
  /** Speed carried out of an ability motion override, × runSpeed. */
  overrideExitSpeed: 1.1,
  /** Upward speed kept after an override ends, × airJumpVelocity. */
  overrideExitRise: 0.6,
  /** Landing impact (px/s) that counts as a heavy landing (sfx/shake). Matches the onLand item trigger. */
  heavyLanding: 900,
} as const;

/** MovementProfile scaled by stats; what the controller actually uses. */
export interface MoveTuning {
  runSpeed: number;
  groundAccel: number;
  groundDecel: number;
  airAccel: number;
  airDecel: number;
  jumpVelocity: number;
  airJumpVelocity: number;
  gravity: number;
  fallGravityMult: number;
  apexGravityMult: number;
  apexThreshold: number;
  maxFall: number;
  jumpCutMult: number;
  coyoteTime: number;
  jumpBuffer: number;
  dashSpeed: number;
  dashDuration: number;
  dashIFrames: number;
  dashRecovery: number;
  dashCooldown: number;
  glideFallSpeed: number | null;
  airJumps: number;
  airDashes: number;
}

type MoveStats = Pick<
  StatBlock,
  'moveSpeed' | 'jumpPower' | 'airJumps' | 'airDashes' | 'dashCooldown' | 'dashPower'
>;

export function deriveTuning(p: MovementProfile, s: Readonly<MoveStats>): MoveTuning {
  const move = Math.max(0.2, s.moveSpeed);
  const jump = Math.max(0.3, s.jumpPower);
  const dash = Math.max(0.2, s.dashPower);
  return {
    runSpeed: p.runSpeed * move,
    // Accelerations scale with speed so the time to reach top speed (the "snap") stays constant.
    groundAccel: p.groundAccel * move,
    groundDecel: p.groundDecel * move,
    airAccel: p.airAccel * move,
    airDecel: p.airDecel * move,
    jumpVelocity: p.jumpVelocity * jump,
    airJumpVelocity: p.airJumpVelocity * jump,
    gravity: p.gravity,
    fallGravityMult: p.fallGravityMult,
    apexGravityMult: p.apexGravityMult,
    apexThreshold: p.apexThreshold,
    maxFall: p.maxFall,
    jumpCutMult: p.jumpCutMult,
    coyoteTime: p.coyoteTime,
    jumpBuffer: p.jumpBuffer,
    // dashPower scales distance: same duration, proportionally faster.
    dashSpeed: p.dashSpeed * dash,
    dashDuration: p.dashDuration,
    dashIFrames: p.dashIFrames,
    dashRecovery: p.dashRecovery,
    dashCooldown: p.dashCooldown * Math.max(0.1, s.dashCooldown),
    glideFallSpeed: p.glideFallSpeed ?? null,
    airJumps: Math.max(0, Math.round(s.airJumps)),
    airDashes: Math.max(0, Math.round(s.airDashes)),
  };
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Move `v` toward `target` by at most `maxDelta` (never overshoots). */
export function approach(v: number, target: number, maxDelta: number): number {
  if (v < target) return Math.min(target, v + maxDelta);
  if (v > target) return Math.max(target, v - maxDelta);
  return target;
}

/**
 * One horizontal velocity step. Accelerates toward moveX × runSpeed; stopping,
 * turning around and shedding excess speed use the (stronger) deceleration so
 * stops are crisp rather than slidey. `control` scales the rate (dash
 * recovery, knockback).
 */
export function stepHorizontal(
  vx: number,
  moveX: number,
  t: Readonly<MoveTuning>,
  onGround: boolean,
  control: number,
  dt: number,
): number {
  const input = clamp(moveX, -1, 1);
  const target = input * t.runSpeed;
  const accel = onGround ? t.groundAccel : t.airAccel;
  const decel = onGround ? t.groundDecel : t.airDecel;
  let rate: number;
  if (Math.abs(input) < 0.01) {
    rate = decel;
  } else if (vx !== 0 && Math.sign(vx) !== Math.sign(target)) {
    // Turning: on the ground brake hard; in the air use whichever is stronger so air control stays responsive.
    rate = onGround ? decel : Math.max(accel, decel);
  } else if (Math.abs(vx) > Math.abs(target)) {
    rate = decel;
  } else {
    rate = accel;
  }
  return approach(vx, target, rate * control * dt);
}

/**
 * Gravity multiplier for the current vertical state:
 * apex hang while a jump is held near the top of its arc, heavier gravity when falling.
 */
export function gravityMultiplier(vy: number, jumpHeld: boolean, jumpActive: boolean, t: Readonly<MoveTuning>): number {
  if (jumpActive && jumpHeld && Math.abs(vy) < t.apexThreshold) return t.apexGravityMult;
  if (vy > 0) return t.fallGravityMult;
  return 1;
}

/**
 * Phase in [0,1) for the 8-frame jump sheet (takeoff, rise, apex, fall).
 * sinceTakeoff = seconds since the jump started (Infinity after walking off a ledge).
 */
export function jumpPhase(vy: number, sinceTakeoff: number, t: Readonly<MoveTuning>): number {
  if (sinceTakeoff < FEEL.takeoffTime) return 0.249 * clamp01(sinceTakeoff / FEEL.takeoffTime);
  const a = Math.max(1, t.apexThreshold);
  if (vy <= -a) {
    const span = Math.max(1, t.jumpVelocity - a);
    return 0.25 + 0.249 * clamp01(1 - (-vy - a) / span);
  }
  if (vy < a) return 0.5 + 0.249 * ((vy + a) / (2 * a));
  return 0.75 + 0.249 * clamp01((vy - a) / 600);
}

/** Landing squash amount (negative = squash) for an impact speed, capped at FEEL.maxSquash. */
export function landingSquash(impact: number, maxFall: number): number {
  if (impact <= 0) return 0;
  const k = clamp01(impact / Math.max(1, maxFall));
  return -FEEL.maxSquash * Math.max(0.35, Math.pow(k, 0.7));
}

/** Total time a dash locks the dash state machine (dash + recovery). */
export function dashTotal(t: Readonly<MoveTuning>): number {
  return t.dashDuration + t.dashRecovery;
}

/** Apex height of a jump with constant rise gravity (analytic, for tests and tooling). */
export function riseHeight(v0: number, gravity: number): number {
  return (v0 * v0) / (2 * Math.max(1, gravity));
}
